import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { exportPlannedLessons } from '@platform/client';
import { useState } from 'react';

import { useTrackedMutation } from '../actions/ActionsProvider';

import { exportScope } from './selection';
import { filtersOf } from './view';

import type { Selection } from './selection';
import type { View } from './view';
import type { PlannedLessonExportQuery } from '@platform/schema/curriculum';

type Mode = PlannedLessonExportQuery['mode'];

/** The CSV goes through the SDK for its token, so the browser is handed a blob to save. */
function save(name: string, csv: string): void {
  const url = URL.createObjectURL(
    new Blob([csv], { type: 'text/csv;charset=utf-8' }),
  );
  const link = document.createElement('a');
  link.href = url;
  link.download = name;
  link.click();
  setTimeout(() => {
    URL.revokeObjectURL(url);
  }, 0);
}

export function ExportMenu({
  view,
  selection,
  counts,
}: {
  view: View;
  selection: Selection;
  counts: Record<Mode, number>;
}) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  const download = useTrackedMutation('Export the curriculum', {
    mutationFn: async (mode: Mode) => {
      const { sort, order, limit, offset } = view;
      const { data } = await exportPlannedLessons({
        query: {
          mode,
          sort,
          order,
          ...(mode !== 'all' && filtersOf(view)),
          ...(mode === 'page' && { limit, offset }),
          ...(mode === 'selected' && exportScope(selection)),
        },
        throwOnError: true,
      });
      save(`curriculum-${mode}.csv`, data);
    },
  });

  const item = (mode: Mode, label: string) => {
    const count = counts[mode];
    return (
      <MenuItem
        disabled={count === 0}
        onClick={() => {
          setAnchor(null);
          download.mutate(mode);
        }}
      >
        {label} ({count.toLocaleString()})
      </MenuItem>
    );
  };

  return (
    <>
      <Button
        variant="outlined"
        disabled={download.isPending}
        aria-haspopup="menu"
        onClick={(event) => {
          setAnchor(event.currentTarget);
        }}
      >
        Export CSV
      </Button>
      <Menu
        anchorEl={anchor}
        open={anchor !== null}
        onClose={() => {
          setAnchor(null);
        }}
      >
        {item('page', 'This page')}
        {item('filtered', 'All filtered results')}
        {item('selected', 'Selected lessons')}
        {item('all', 'The whole curriculum')}
      </Menu>
    </>
  );
}
